import { Link } from "react-router-dom";
import {
  PATH_PRIVACY_POLICY,
  PATH_TERMS_AND_CONDITIONS,
  PATH_CONTACT,
  PATH_FAQ,
  PATH_ABOUT,
  PATH_ERGOTHERAPIE,
  PATH_NEUROLOGIE_GERIATRIE,
  PATH_PSYCHIATRIE,
  PATH_PEDIATRICS,
  PATH_ADDITIONAL_OFFERS,
  PATH_GROUP_THERAPY,
  PATH_DIRECTIONS_HOURS,
  PATH_PRACTICE,
} from "../../utils/constants";
import { MapPinIcon, PhoneIcon, EnvelopeIcon, ClockIcon } from "@heroicons/react/24/outline";
import { FaInstagram } from "react-icons/fa";
import { LogoSvg } from "../svgs/LogoSvg";
import { ThemeSwitcher } from "../common/ThemeSwitcher";

const footerLinkClass =
  "text-sm text-gray-500 dark:text-gray-400 hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors duration-200";

export const Footer = () => {
  return (
    <footer aria-label="Fußbereich" className="bg-gray-50/70 dark:bg-gray-900/40 border-t border-gray-200/60 dark:border-gray-700/40">
      <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12">
        {/* Top: Brand + Columns */}
        <div className="pt-14 pb-10">
          {/* Brand row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-12">
            <Link to="/" className="flex items-center gap-3.5 group">
              <LogoSvg classNames="h-9 w-auto fill-current text-ergo-500 dark:text-ergo-400 transition-transform duration-300 group-hover:scale-105" />
              <div className="flex flex-col">
                <span className="text-sm font-medium tracking-wider text-gray-900 dark:text-white">
                  ERGOTHERAPIE
                </span>
                <span className="text-sm font-medium tracking-wider text-ergo-600 dark:text-ergo-400">
                  UND ZEIT(T)RÄUME
                </span>
              </div>
            </Link>
            <p className="max-w-xs text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              Ganzheitliche und individuelle Ergotherapie in Rottweil — für alle Altersgruppen.
            </p>
          </div>

          {/* Link Columns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-10">
            {/* Column 1: Leistungen */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                Leistungen
              </h3>
              <ul className="space-y-2.5">
                <li><Link to={PATH_ERGOTHERAPIE} className={footerLinkClass}>Ergotherapie</Link></li>
                <li><Link to={PATH_NEUROLOGIE_GERIATRIE} className={footerLinkClass}>Neurologie / Geriatrie</Link></li>
                <li><Link to={PATH_PSYCHIATRIE} className={footerLinkClass}>Psychiatrie</Link></li>
                <li><Link to={PATH_PEDIATRICS} className={footerLinkClass}>Pädiatrie</Link></li>
                <li><Link to={PATH_GROUP_THERAPY} className={footerLinkClass}>Gruppentherapien</Link></li>
                <li><Link to={PATH_ADDITIONAL_OFFERS} className={footerLinkClass}>Zusatzangebote</Link></li>
              </ul>
            </div>

            {/* Column 2: Praxis */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                Praxis
              </h3>
              <ul className="space-y-2.5">
                <li><Link to={PATH_ABOUT} className={footerLinkClass}>Unser Team</Link></li>
                <li><Link to={PATH_PRACTICE} className={footerLinkClass}>Praxisräume</Link></li>
                <li><Link to={PATH_DIRECTIONS_HOURS} className={footerLinkClass}>Anfahrt & Zeiten</Link></li>
                <li><Link to={PATH_FAQ} className={footerLinkClass}>Häufige Fragen</Link></li>
                <li><Link to={PATH_CONTACT} className={footerLinkClass}>Kontakt</Link></li>
              </ul>
            </div>

            {/* Column 3: Kontakt */}
            <div className="col-span-2 sm:col-span-1 lg:col-span-2">
              <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                Kontakt
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Address & Hours */}
                <div className="space-y-4 text-sm text-gray-500 dark:text-gray-400">
                  <a
                    href="https://www.google.com/maps/place/ERGOTHERAPIE+UND+ZEIT(T)R%C3%84UME/@48.158268,8.6335273,18z/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2.5 group"
                  >
                    <MapPinIcon className="h-4 w-4 text-ergo-500/70 dark:text-ergo-400/70 mt-0.5 flex-shrink-0" />
                    <span className="group-hover:text-ergo-600 dark:group-hover:text-ergo-400 transition-colors">
                      Auf dem Wall 29, 78628 Rottweil
                    </span>
                  </a>
                  <div className="flex items-start gap-2.5">
                    <ClockIcon className="h-4 w-4 text-ergo-500/70 dark:text-ergo-400/70 mt-0.5 flex-shrink-0" />
                    <div>
                      <p>Mo – Fr: 08:00 – 18:00</p>
                      <p>Sa: 07:00 – 17:00</p>
                    </div>
                  </div>
                </div>

                {/* Phone, Email, Social */}
                <div className="space-y-4 text-sm text-gray-500 dark:text-gray-400">
                  <a
                    href="tel:+4974189698293"
                    className="flex items-center gap-2.5 hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors"
                  >
                    <PhoneIcon className="h-4 w-4 text-ergo-500/70 dark:text-ergo-400/70 flex-shrink-0" />
                    0741 89 69 82 93
                  </a>
                  <a
                    href="mailto:info@ergo-rw.de"
                    className="flex items-center gap-2.5 hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors"
                  >
                    <EnvelopeIcon className="h-4 w-4 text-ergo-500/70 dark:text-ergo-400/70 flex-shrink-0" />
                    info@ergo-rw.de
                  </a>
                  <a
                    href="https://www.instagram.com/ergotherapie_rottweil"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors"
                  >
                    <FaInstagram className="h-4 w-4 text-ergo-500/70 dark:text-ergo-400/70 flex-shrink-0" />
                    @ergotherapie_rottweil
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200/60 dark:border-gray-700/40">
        <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                © 2023 – 2026 Ergotherapie und Zeit(t)räume
              </p>
              <ThemeSwitcher />
            </div>
            <div className="flex items-center gap-5">
              <Link
                to={PATH_TERMS_AND_CONDITIONS}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors"
              >
                Impressum
              </Link>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <Link
                to={PATH_PRIVACY_POLICY}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors"
              >
                Datenschutz
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
